import type { VueCookies } from 'vue-cookies';

import { clearHandlingFeeLimitCookieFunction } from '@/components/handling-fee-limit-banner-fi/handlingFeeCookie';
import { sendEventFunction } from '@/composables/eventHub';
import { usernameCookie } from '@/composables/login';
import { clearRefreshFunction, pendingTokenRefresh } from '@/composables/tokenRefresh';
import type { FischerPlugin } from '@/lib/fischerPlugin';
import { isInvalidTokenError } from '@/utils/apolloTools';
import { sendSessionEndedMessage } from '@/utils/assistedServiceBroadcast';
import { markAsNotIDS } from '@/utils/idsTools';

import { useCookies } from './cookies';
import { useFischer } from './fischerPlugin';

export interface LogoutReason {
  reason: string;
}

export const logoutFromSSO = (redirectRelative: string, clientId: string): Promise<void> => {
  const redirect = new URL(redirectRelative, window.location.origin);
  window.location.href = `${import.meta.env.VITE_OPENID_SERVICES}authorizationserver/connect/logout?${new URLSearchParams(
    {
      post_logout_redirect_uri: String(redirect),
      client_id: clientId,
    },
  )}`;

  return new Promise(() => {});
};

export const logoutFunction = async (
  { reason, successUrl }: { reason?: string; successUrl?: string } = {},
  fischer: FischerPlugin,
  cookies: VueCookies,
  setErrorOccured?: () => void,
) => {
  if (IS_SSR) {
    return;
  }

  // Notify other tabs that the session is ending
  if (fischer.user.getAssistedService()?.user) {
    sendSessionEndedMessage();
  }

  const logoutSuccessPage = successUrl || fischer.pages.logoutSuccess().url;
  const redirect = reason ? `${logoutSuccessPage}?reason=${reason}` : logoutSuccessPage;
  try {
    cookies.remove(usernameCookie);
    fischer.setAssistedService(undefined, undefined, `logout:${reason || 'user'}`);
    clearHandlingFeeLimitCookieFunction(cookies);
    clearRefreshFunction(fischer);

    if (IS_SHOWROOM) {
      cookies.remove('AccessToken');
      cookies.remove('IdToken');
      cookies.remove('CartId');
    } else {
      markAsNotIDS();
      await fetch('/webapi/shop/userapi/Logout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
    }
    await sendEventFunction(
      fischer,
      cookies,
      'custom',
      {},
      'logout',
      reason ? { reason } : undefined,
    );
    if (reason !== 'quote-order-complete') {
      await logoutFromSSO(redirect, fischer.getClientId());
    }
  } catch (error) {
    console.error(error);
    setErrorOccured?.();
  }
};

// a call that failed on a token the running refresh renews is no reason to log out
const isRenewedByRefresh = async () => (await pendingTokenRefresh()) === true;

export const logoutIfTokenInvalidFunction = async (
  error: any, // note: making the type 'any' so consumers can just pipe in the argument of the catch block
  fischer: FischerPlugin,
  logout: (reason: LogoutReason) => Promise<void>,
) => {
  if (
    (isInvalidTokenError(error) || fischer.user.isIdTokenValid()) &&
    !(await isRenewedByRefresh())
  ) {
    await logout({ reason: 'InvalidToken' });
  }
};

export const ensureTokenValidFunction = async (
  fischer: FischerPlugin,
  logout: (reason: LogoutReason) => Promise<void>,
): Promise<boolean> => {
  if (fischer.user.isIdTokenValid() && !(await isRenewedByRefresh())) {
    await logout({ reason: 'InvalidToken' });
    return false;
  }
  return true;
};

export const useUser = () => {
  const fischer = useFischer();
  const { cookies } = useCookies();

  const logout = (params?: Parameters<typeof logoutFunction>[0]) =>
    logoutFunction(params, fischer, cookies);

  const logoutIfTokenInvalid = (error: Parameters<typeof logoutIfTokenInvalidFunction>[0]) =>
    logoutIfTokenInvalidFunction(error, fischer, logout);

  const ensureTokenValid = () => ensureTokenValidFunction(fischer, logout);

  return {
    logoutIfTokenInvalid,
    logout,
    logoutFromSSO,
    ensureTokenValid,
  };
};
